import { useEffect } from "react";
import "./KeyboardShortcutModal.css";

interface KeyboardShortcutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShortcutEntry {
  keys: string;
  description: string;
}

const SHORTCUTS: { section: string; entries: ShortcutEntry[] }[] = [
  {
    section: "FIELD NAVIGATION",
    entries: [
      { keys: "Tab / Enter", description: "Next field" },
      { keys: "Shift+Tab", description: "Previous field" },
      { keys: "Esc", description: "Defer a blocked field — move on, fix it before submit" },
      { keys: "Ctrl+O", description: "Override the active failing field (when allowed)" },
      { keys: "Ctrl+E", description: "Swap to the other OCR engine's candidate (handwriting disagreement — toggle)" },
      { keys: "Ctrl+J", description: "Jump to the next field with low-confidence characters" },
    ],
  },
  {
    section: "DOCUMENT ACTIONS",
    entries: [
      { keys: "Ctrl+Enter", description: "Accept document" },
      { keys: "Ctrl+Shift+Enter", description: "Accept and get next" },
      { keys: "Ctrl+R", description: "Reject document" },
      { keys: "Ctrl+H", description: "Hold document" },
    ],
  },
  {
    section: "VIEW CONTROLS",
    entries: [
      { keys: "Ctrl+; (or F2)", description: "Cycle snippet zoom" },
      { keys: "F5 / F", description: "Toggle focus mode" },
      { keys: "R", description: "Rotate document" },
      { keys: "W", description: "Fit to width" },
      { keys: "+ / -", description: "Zoom in / out" },
      { keys: "Ctrl+1-9", description: "Jump to page" },
    ],
  },
  {
    section: "MODE TOGGLES",
    entries: [
      { keys: "Ctrl+B", description: "Toggle terminal mode" },
      { keys: "Ctrl+M", description: "Toggle audio feedback" },
      { keys: "?", description: "Show this shortcut reference" },
    ],
  },
];

export function KeyboardShortcutModal({ isOpen, onClose }: KeyboardShortcutModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape" || e.key === "?") {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="ks-overlay" onClick={onClose} data-testid="keyboard-shortcut-modal">
      <div className="ks-card" onClick={(e) => e.stopPropagation()}>
        <div className="ks-header">
          <span className="ks-title">KEYBOARD SHORTCUTS</span>
          <button className="ks-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="ks-body">
          {SHORTCUTS.map((section) => (
            <div key={section.section} className="ks-section">
              <div className="ks-section-label">{section.section}</div>
              {section.entries.map((entry) => (
                <div key={entry.keys} className="ks-row">
                  <kbd className="ks-keys">{entry.keys}</kbd>
                  <span className="ks-desc">{entry.description}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
